import React, { useMemo, useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DashboardLayout from '../../layout/DashboardLayout';
import useAgentic from '../../hooks/useAgentic';
import Sessions from './sessions';

const PHASE_LABELS = {
  input: 'User Message',
  classifier: 'Classification',
  router: 'Routing',
  agent_run: 'Agent Execution',
  tool_call: 'Tool Call',
  tool_result: 'Tool Result',
  assistant_output: 'Assistant Message',
  system_event: 'System Event',
  error: 'Error',
};

const STATUS_COLOR = {
  started: 'default',
  running: 'warning',
  completed: 'success',
  failed: 'error',
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString();
  } catch (error) {
    return dateString;
  }
};

function AgenticPage() {
  const [prompt, setPrompt] = useState('');
  const [sessionKey, setSessionKey] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('info');
  const [showMetaRows, setShowMetaRows] = useState(false);

  const {
    sessions,
    sessionsPagination,
    selectedSessionId,
    selectedSession,
    interactions,
    timelinePagination,
    latestRun,
    sessionsLoading,
    detailsLoading,
    runLoading,
    error,
    lastMessageInput,
    setSelectedSessionId,
    loadSessions,
    loadSessionDetails,
    sendMessage,
    retryLastMessage,
  } = useAgentic();

  useEffect(() => {
    if (!error?.message) return;
    setToastSeverity('error');
    setToastMessage(error.message);
    setToastOpen(true);
  }, [error]);

  const orderedTimelineItems = useMemo(() => {
    return [...(interactions || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  }, [interactions]);

  const chatMessages = useMemo(() => {
    return orderedTimelineItems.filter(
      (item) =>
        (item?.role === 'user' || item?.role === 'assistant') &&
        Boolean((item?.contentText || '').trim())
    );
  }, [orderedTimelineItems]);

  const metadataRows = useMemo(() => {
    return orderedTimelineItems.filter(
      (item) => !(item?.role === 'user' || item?.role === 'assistant')
    );
  }, [orderedTimelineItems]);

  const handleSend = async (event) => {
    event.preventDefault();

    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      setToastSeverity('warning');
      setToastMessage('Please enter a message.');
      setToastOpen(true);
      return;
    }

    try {
      await sendMessage({
        input: trimmedPrompt,
        sessionKey: sessionKey.trim() || undefined,
      });

      setPrompt('');
      setToastSeverity('success');
      setToastMessage('Message sent successfully.');
      setToastOpen(true);
    } catch (sendError) {
      // Error toast handled by hook state
    }
  };

  const handleSelectSession = async (sessionId) => {
    setSelectedSessionId(sessionId);
    await loadSessionDetails(sessionId, 1, timelinePagination.limit || 200);
  };

  const handleSessionsPage = async (nextPage) => {
    if (nextPage < 1 || nextPage > (sessionsPagination.totalPages || 1)) return;
    await loadSessions(nextPage, sessionsPagination.limit || 20);
  };

  const handleTimelinePage = async (nextPage) => {
    if (!selectedSessionId) return;
    if (nextPage < 1 || nextPage > (timelinePagination.totalPages || 1)) return;
    await loadSessionDetails(selectedSessionId, nextPage, timelinePagination.limit || 200);
  };

  const latestCategory = latestRun?.category || selectedSession?.classifierCategory || '-';
  const latestDuration = latestRun?.durationMs;

  return (
    <DashboardLayout pageName="Agentic">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', m:2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Agentic
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start a new conversation or continue an existing one with full timeline history.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
          <Sessions />

          <Box sx={{ minWidth: 0 }}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Chip size="small" label={`Category: ${latestCategory}`} />
                  <Chip
                    size="small"
                    label={`Status: ${selectedSession?.status || 'idle'}`}
                    color={STATUS_COLOR[selectedSession?.status] || 'default'}
                  />
                  {latestDuration !== undefined && latestDuration !== null && (
                    <Chip size="small" label={`Turn duration: ${latestDuration} ms`} />
                  )}
                </Stack>

                <Box
                  sx={{
                    minHeight: 280,
                    maxHeight: 430,
                    overflowY: 'auto',
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    p: 1.5,
                    mb: 2,
                  }}
                >
                  {detailsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : !selectedSessionId ? (
                    <Typography variant="body2" color="text.secondary">
                      Send your first message to start a new chat.
                    </Typography>
                  ) : chatMessages.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No user/assistant messages found for this session yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1.2}>
                      {chatMessages.map((item) => {
                        const isUser = item.role === 'user';
                        return (
                          <Box
                            key={`${item._id || item.sequence}-bubble`}
                            sx={{
                              display: 'flex',
                              justifyContent: isUser ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: '82%',
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                bgcolor: isUser ? 'primary.main' : 'background.paper',
                                color: isUser ? 'primary.contrastText' : 'text.primary',
                                border: isUser ? 'none' : '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                {isUser ? 'You' : (item.agentName || 'Assistant')} · {formatDate(item.createdAt)}
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {item.contentText}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>

                <Box component="form" onSubmit={handleSend}>
                  {!selectedSessionId && (
                    <TextField
                      fullWidth
                      label="Session Key (optional)"
                      value={sessionKey}
                      onChange={(event) => setSessionKey(event.target.value)}
                      sx={{ mb: 1 }}
                    />
                  )}
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    label={selectedSessionId ? 'Send follow-up message' : 'Start a new conversation'}
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Button type="submit" variant="contained" disabled={runLoading}>
                      {runLoading ? 'Sending...' : 'Send'}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      disabled={!lastMessageInput || runLoading}
                      onClick={async () => {
                        try {
                          await retryLastMessage();
                          setToastSeverity('success');
                          setToastMessage('Retried last message successfully.');
                          setToastOpen(true);
                        } catch (error) {
                          // handled by hook + toast effect
                        }
                      }}
                    >
                      Retry Last
                    </Button>
                  </Stack>
                </Box>

                {selectedSession?.createdAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Session started: {formatDate(selectedSession.createdAt)}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6">Timeline Metadata</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      disabled={!selectedSessionId}
                      onClick={() => loadSessionDetails(selectedSessionId, timelinePagination.page || 1, timelinePagination.limit || 200)}
                    >
                      Refresh
                    </Button>
                    <Button size="small" onClick={() => setShowMetaRows((prev) => !prev)}>
                      {showMetaRows ? 'Hide' : 'Show'}
                    </Button>
                  </Stack>
                </Stack>

                <Collapse in={showMetaRows}>
                  {metadataRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No system/tool metadata rows in this timeline.
                    </Typography>
                  ) : (
                    <List disablePadding>
                      {metadataRows.map((item) => (
                        <React.Fragment key={`${item._id || item.sequence}-meta`}>
                          <Box sx={{ py: 1.2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              #{item.sequence || '-'} · {PHASE_LABELS[item.phase] || item.phase || 'Unknown'} · {item.agentName || item.role || 'Meta'} · {formatDate(item.createdAt)}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {(item.contentText || '').trim() || '-'}
                            </Typography>
                          </Box>
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Collapse>

                <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={(timelinePagination.page || 1) <= 1 || detailsLoading || !selectedSessionId}
                    onClick={() => handleTimelinePage((timelinePagination.page || 1) - 1)}
                  >
                    Previous
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Page {timelinePagination.page || 1} of {timelinePagination.totalPages || 1}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={(timelinePagination.page || 1) >= (timelinePagination.totalPages || 1) || detailsLoading || !selectedSessionId}
                    onClick={() => handleTimelinePage((timelinePagination.page || 1) + 1)}
                  >
                    Next
                  </Button>
                </Stack>
              </CardContent>
            </Card> */}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3500}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          variant="filled"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={async () => {
                if (!lastMessageInput) return;
                try {
                  await retryLastMessage();
                  setToastOpen(false);
                } catch (retryError) {
                  // hook error handling
                }
              }}
              disabled={!lastMessageInput || runLoading}
            >
              Retry
            </Button>
          }
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default AgenticPage;
